import {
  required,
  maxLength,
  minLength,
  integer,
  email,
  url,
  sameAs
} from 'vuelidate/lib/validators'

import {
  englishOrChineseCharactersOnly,
  phoneIncludeSomeCharactersOnly,
  englishOrCharactersOnly
} from '~utils/validations'

export const wordLimit = {
  enterprise_owner: 15,
  enterprise_employee_number: 10,
  enterprise_registered_capital: 10,
  enterprise_services: 126,
  enterprise_hr_contact_person: 15,
  enterprise_hr_contact_email: 50,
  enterprise_hr_contact_tel: 20,
  enterprise_tel: 20,
  enterprise_fax: 20,
  enterprise_website: 80,
  enterprise_address: 30,
  enterprise_related_company: 20,
  enterprise_employee_benefits: 228,
  enterprise_employee_training_introduction: 228,
  enterprise_recruiting_description: 228,
  enterprise_work_portfolio: 50,
  enterprise_remark: 50,
  enterprise_services_en: 374,
  enterprise_employee_benefits_en: 576,
  enterprise_employee_training_introduction_en: 576,
  enterprise_recruiting_description_en: 576,
  enterprise_work_portfolio_en: 100,
  enterprise_note_en: 100,
  enterprise_register_capital: 10,
  enterprise_introduction: 228
}

const basicInfoValidations = {
  enterprise_gui_number: { required },
  enterprise_name: { required },
  enterprise_owner: {
    required,
    englishOrChineseCharactersOnly,
    maxLength: maxLength(wordLimit.enterprise_owner)
  },
  enterprise_founded_date: { required }, // TODO: 日期格式
  enterprise_employee_number: {
    required,
    integer,
    maxLength: maxLength(wordLimit.enterprise_employee_number)
  },
  enterprise_registered_capital: {
    required,
    integer,
    maxLength: maxLength(wordLimit.enterprise_registered_capital)
  },
  enterprise_industry: { required },
  // enterprise_industry_description: { required },
  enterprise_website: {
    required,
    url,
    maxLength: maxLength(wordLimit.enterprise_website)
  },
  enterprise_address: {
    required,
    maxLength: maxLength(wordLimit.enterprise_address)
  },
  enterprise_tel: {
    required,
    phoneIncludeSomeCharactersOnly,
    maxLength: maxLength(wordLimit.enterprise_tel)
  },
  enterprise_fax: {
    phoneIncludeSomeCharactersOnly,
    maxLength: maxLength(wordLimit.enterprise_fax)
  },
  enterprise_hire_interns: {},
  enterprise_hire_foreigners: {},
  enterprise_hire_disabled_people: {},
  enterprise_introduction: {
    required,
    maxLength: maxLength(wordLimit.enterprise_introduction)
  },
  enterprise_logo: {},
  enterprise_shift_schedule: {
    required,
    minLength: minLength(1)
  },
  enterprise_is_nine_to_five: {},
  enterprise_is_shift_work: {},
  enterprise_is_flextime: {},
  enterprise_remark: {
    maxLength: maxLength(wordLimit.enterprise_remark)
  },
  enterprise_register_capital: {
    maxLength: maxLength(wordLimit.enterprise_register_capital)
  }
}

const specialIssueValidations = {
  enterprise_related_company: {
    required,
    maxLength: maxLength(wordLimit.enterprise_related_company)
  },
  enterprise_services: {
    required,
    maxLength: maxLength(wordLimit.enterprise_services)
  },
  enterprise_employee_benefits: {
    required,
    maxLength: maxLength(wordLimit.enterprise_employee_benefits)
  },
  enterprise_employee_training_introduction: {
    required,
    maxLength: maxLength(wordLimit.enterprise_employee_training_introduction)
  },
  enterprise_recruiting_description: {
    required,
    maxLength: maxLength(wordLimit.enterprise_recruiting_description)
  },
  enterprise_hr_contact_tel: {
    required,
    phoneIncludeSomeCharactersOnly,
    maxLength: maxLength(wordLimit.enterprise_hr_contact_tel)
  },
  enterprise_hr_contact_person: {
    required,
    englishOrChineseCharactersOnly,
    maxLength: maxLength(wordLimit.enterprise_hr_contact_person)
  },
  enterprise_hr_contact_email: {
    required,
    email,
    maxLength: maxLength(wordLimit.enterprise_hr_contact_email)
  },
  enterprise_work_portfolio: {
    required,
    maxLength: maxLength(wordLimit.enterprise_work_portfolio)
  }
}

const specialIssueInEnglishRequired = {
  enterprise_name_en: {
    required,
    englishOrCharactersOnly
  },
  enterprise_address_en: {
    required,
    englishOrCharactersOnly
  },
  enterprise_hr_contact_person_en: {
    required,
    englishOrCharactersOnly
  },
  enterprise_related_company_en: {
    required,
    englishOrCharactersOnly
  },
  enterprise_services_en: {
    required,
    englishOrCharactersOnly,
    maxLength: maxLength(wordLimit.enterprise_services_en)
  },
  enterprise_employee_benefits_en: {
    required,
    englishOrCharactersOnly,
    maxLength: maxLength(wordLimit.enterprise_employee_benefits_en)
  },
  enterprise_employee_training_introduction_en: {
    required,
    englishOrCharactersOnly,
    maxLength: maxLength(wordLimit.enterprise_employee_training_introduction_en)
  },
  enterprise_recruiting_description_en: {
    required,
    englishOrCharactersOnly,
    maxLength: maxLength(576)
  },
  enterprise_note_en: {
    required,
    englishOrCharactersOnly,
    maxLength: maxLength(wordLimit.enterprise_note_en)
  },
  enterprise_work_portfolio_en: {
    required,
    englishOrCharactersOnly,
    maxLength: maxLength(wordLimit.enterprise_work_portfolio_en)
  }
}

const specialIssueInEnglishNotRequired = {
  enterprise_name_en: {
    required,
    englishOrCharactersOnly
  },
  enterprise_address_en: {
    englishOrCharactersOnly
  },
  enterprise_hr_contact_person_en: {
    englishOrCharactersOnly
  },
  enterprise_related_company_en: {
    englishOrCharactersOnly
  },
  enterprise_services_en: {
    englishOrCharactersOnly,
    maxLength: maxLength(wordLimit.enterprise_services_en)
  },
  enterprise_employee_benefits_en: {
    englishOrCharactersOnly,
    maxLength: maxLength(wordLimit.enterprise_employee_benefits_en)
  },
  enterprise_employee_training_introduction_en: {
    englishOrCharactersOnly,
    maxLength: maxLength(wordLimit.enterprise_employee_training_introduction_en)
  },
  enterprise_recruiting_description_en: {
    englishOrCharactersOnly,
    maxLength: maxLength(576)
  },
  enterprise_note_en: {
    englishOrCharactersOnly,
    maxLength: maxLength(wordLimit.enterprise_note_en)
  },
  enterprise_work_portfolio_en: {
    englishOrCharactersOnly,
    maxLength: maxLength(wordLimit.enterprise_work_portfolio_en)
  }
}

const password = {
  password: {
    minLength: minLength(6)
  },
  password_confirmation: {
    sameAsPassword: sameAs('password')
  }
}

export default function enterpriseValidations (doesHireForeigners) {
  if (!doesHireForeigners) {
    return {
      enterprise: {
        ...basicInfoValidations,
        ...specialIssueValidations,
        ...specialIssueInEnglishNotRequired,
        ...password
      }
    }
  } else {
    return {
      enterprise: {
        ...basicInfoValidations,
        ...specialIssueValidations,
        ...specialIssueInEnglishRequired,
        ...password
      }
    }
  }
}
